import { NgModule } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { ConfirmationPersistenceEffects } from './effects/confirmation-persistence.effects';
import { ConfirmationQueryEffects } from './effects/confirmation-query.effects';
import { confirmationCollectionReducer } from './reducers/confirmation.reducer';
import { CONFIRMATION_COLLECTION_FEATURE_KEY } from './confirmation-collection.state';
import { PERSISTED_SLICES, PersistedSlice } from '../persistence/persistence.registry';
import { selectConfirmations } from './selectors/confirmation.selector';

/**
 * Registers the confirmation feature slice. Imported from `AppModule` (not the routed
 * `ConfirmationModule`) because its purge effect listens for app-level notification-list changes,
 * and because the slice is read outside the confirmation route too — the timesheet derives its
 * in-progress entries from it.
 *
 * The slice does not start itself. `ConfirmationIsLoadedGuard` dispatches `initConfirmations` the first
 * time a route that needs it activates — the confirmation, the timesheet or PACE Spector — and waits for
 * the slice to settle; that on-demand start replaced a start on registration in SHP-9806 (values not
 * restored after a reload or a log out and in). `ConfirmationPersistenceEffects` then asks the generic
 * persistence layer to hydrate the slice, migrates the legacy confirmations into it, and persists it
 * again on every change. The registration below is that layer's plain projection mode: the whole
 * collection is one concurrency-store entry, like every other persisted slice.
 * `ConfirmationQueryEffects` owns the read-only lookups (recently-completed list, calibration date)
 * that keep the facade free of collaborator calls. `ConfirmationSubmitEffects` is registered by the
 * app instead of here: it drives the submit pipeline through app-level collaborators (mileage, sales
 * quotes, the OData confirmation service) that a library may not depend on.
 */
@NgModule({
    imports: [
        StoreModule.forFeature(CONFIRMATION_COLLECTION_FEATURE_KEY, confirmationCollectionReducer),
        EffectsModule.forFeature([ConfirmationPersistenceEffects, ConfirmationQueryEffects]),
    ],
    providers: [
        {
            provide: PERSISTED_SLICES,
            multi: true,
            useValue: {
                featureKey: CONFIRMATION_COLLECTION_FEATURE_KEY,
                persistSelector: selectConfirmations,
            } satisfies PersistedSlice,
        },
    ],
})
export class ConfirmationStoreModule {}
